export const AGING_OPTIONS = [
  { id: 'todas', label: 'Todas' },
  { id: 'vigente', label: 'A vencer' },
  { id: '1-30', label: '1–30 días' },
  { id: '31-60', label: '31–60' },
  { id: '61+', label: '61+ días' },
];

export const GESTION_TIPOS = [
  { id: 'llamado', label: 'Llamado' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'email', label: 'Email' },
  { id: 'promesa', label: 'Promesa' },
  { id: 'nota', label: 'Nota' },
];

function parseDateOnly(value) {
  if (!value) return null;
  const s = String(value).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function dueDateOf(factura) {
  return factura?.vencimiento || factura?.fecha || null;
}

export function daysOverdue(factura, today = new Date()) {
  const due = parseDateOnly(dueDateOf(factura));
  if (!due) return 0;
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((t - due) / 86400000);
}

export function pagosOf(factura) {
  const fromDoc = Array.isArray(factura?.pagos) ? factura.pagos : [];
  const fromMeta = factura?.metadata?.pagos;
  const list = Array.isArray(fromMeta) ? fromMeta : fromDoc;
  return list.slice().sort((a, b) => String(b.fecha || b.createdAt || '').localeCompare(String(a.fecha || a.createdAt || '')));
}

export function montoPagado(factura) {
  const pagos = pagosOf(factura);
  if (pagos.length) {
    return Math.round(pagos.reduce((sum, p) => sum + (Number(p.monto) || 0), 0) * 100) / 100;
  }
  if (factura?.estadoPago === 'pagada') return Number(factura.total) || 0;
  return 0;
}

export function saldoPendiente(factura) {
  const total = Number(factura?.total) || 0;
  return Math.max(0, Math.round((total - montoPagado(factura)) * 100) / 100);
}

export function isFullyPaid(factura) {
  return saldoPendiente(factura) <= 0.009;
}

export function isPendiente(factura) {
  return !isFullyPaid(factura);
}

export function estadoPagoLabel(factura) {
  if (isFullyPaid(factura)) return 'Pagada';
  if (montoPagado(factura) > 0.009) return 'Parcial';
  return 'Impaga';
}

export function agingBucket(factura) {
  if (!factura || isFullyPaid(factura)) return null;
  const days = daysOverdue(factura);
  if (days <= 0) return 'vigente';
  if (days <= 30) return '1-30';
  if (days <= 60) return '31-60';
  return '61+';
}

export function matchesAging(factura, filter) {
  if (!filter || filter === 'todas') return true;
  const bucket = agingBucket(factura);
  if (filter === 'vencidas') return Boolean(bucket && bucket !== 'vigente');
  return bucket === filter;
}

export function agingLabel(factura) {
  const bucket = agingBucket(factura);
  if (!bucket) return null;
  const days = daysOverdue(factura);
  if (bucket === 'vigente') {
    const left = Math.abs(days);
    return left === 0 ? 'Vence hoy' : `Vence en ${left}d`;
  }
  return `${days}d vencida`;
}

export function agingTone(factura, modoOscuro) {
  const bucket = agingBucket(factura);
  if (bucket === 'vigente') {
    return modoOscuro ? 'bg-sky-900/50 text-sky-200' : 'bg-sky-50 text-sky-800';
  }
  if (bucket === '1-30') {
    return modoOscuro ? 'bg-amber-900/50 text-amber-200' : 'bg-amber-50 text-amber-800';
  }
  return modoOscuro ? 'bg-red-900/50 text-red-200' : 'bg-red-50 text-red-800';
}

export function phoneDigits(phone) {
  return String(phone || '').replace(/\D/g, '');
}

export function whatsappHref(phone, text) {
  const digits = phoneDigits(phone);
  if (!digits) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text || '')}`;
}

export { cobranzaWhatsappText } from './whatsappTemplates';

export function gestionesOf(factura) {
  const fromDoc = Array.isArray(factura?.gestiones) ? factura.gestiones : [];
  const fromMeta = factura?.metadata?.gestiones;
  const list = Array.isArray(fromMeta) ? fromMeta : fromDoc;
  return list.slice().sort((a, b) => String(b.fecha || b.createdAt || '').localeCompare(String(a.fecha || a.createdAt || '')));
}

/** ID estable para API y estado (Supabase usa id; el front histórico usa _id). */
export function invoiceId(factura) {
  if (!factura) return null;
  return factura._id || factura.id || null;
}

export function sameInvoiceId(a, bOrId) {
  const left = invoiceId(a);
  const right = typeof bOrId === 'object' && bOrId !== null ? invoiceId(bOrId) : bOrId;
  if (left == null || right == null) return false;
  return String(left) === String(right);
}

/** Alinea pagos/gestiones con metadata tras PUT/POST (evita saldos viejos en UI). */
export function normalizeInvoiceFromApi(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const meta = raw.metadata && typeof raw.metadata === 'object' ? { ...raw.metadata } : {};
  const pagosFromDoc = Array.isArray(raw.pagos) ? raw.pagos : [];
  const pagosFromMeta = meta.pagos;
  const pagos = Array.isArray(pagosFromMeta) ? pagosFromMeta : pagosFromDoc;
  const gestionesFromDoc = Array.isArray(raw.gestiones) ? raw.gestiones : [];
  const gestionesFromMeta = meta.gestiones;
  const gestiones = Array.isArray(gestionesFromMeta) ? gestionesFromMeta : gestionesFromDoc;
  meta.pagos = pagos;
  meta.gestiones = gestiones;
  const id = invoiceId(raw);
  return {
    ...raw,
    ...(id ? { _id: id, id: raw.id || id } : {}),
    pagos,
    gestiones,
    metadata: meta,
  };
}

export function patchInvoiceInList(list, id, patch) {
  const targetId = id != null ? String(id) : '';
  if (!targetId) return list;
  return list.map((row) => {
    if (String(invoiceId(row)) !== targetId) return row;
    return normalizeInvoiceFromApi({ ...row, ...patch });
  });
}
